import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  BRAND_PALETTES,
  BRAND_PALETTE_COOKIE_KEY,
  BRAND_PALETTE_STORAGE_KEY,
  DEFAULT_BRAND_PALETTE,
  isBrandPaletteKey,
  type BrandPalette,
  type BrandPaletteKey,
} from "./brandPalettes";

interface BrandThemeContextValue {
  paletteKey: BrandPaletteKey;
  palette: BrandPalette;
  setPaletteKey: (key: BrandPaletteKey) => void;
}

const BrandThemeContext = createContext<BrandThemeContextValue | null>(null);

function readCookiePalette() {
  if (typeof document === "undefined") {
    return null;
  }

  const value = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${BRAND_PALETTE_COOKIE_KEY}=`))
    ?.split("=")[1];

  return isBrandPaletteKey(value) ? value : null;
}

function readInitialPalette(): BrandPaletteKey {
  if (typeof window === "undefined") {
    return DEFAULT_BRAND_PALETTE;
  }

  const cookieValue = readCookiePalette();
  const storedValue = window.localStorage.getItem(BRAND_PALETTE_STORAGE_KEY);
  return cookieValue ?? (isBrandPaletteKey(storedValue) ? storedValue : DEFAULT_BRAND_PALETTE);
}

function persistPalette(key: BrandPaletteKey) {
  window.localStorage.setItem(BRAND_PALETTE_STORAGE_KEY, key);

  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  const sharedDomain = window.location.hostname.endsWith(".nextgenja.com")
    ? "; Domain=.nextgenja.com"
    : "";
  document.cookie = `${BRAND_PALETTE_COOKIE_KEY}=${key}; Path=/; Max-Age=31536000; SameSite=Lax${secure}${sharedDomain}`;
}

function applyPaletteVariables(palette: BrandPalette) {
  const root = document.documentElement;
  root.dataset.brandPalette = palette.key;
  root.style.setProperty("--evolys-brand-primary", palette.primary);
  root.style.setProperty("--evolys-brand-on-primary", palette.onPrimary);
  root.style.setProperty("--evolys-brand-accent", palette.accent);
  root.style.setProperty("--evolys-brand-ink", palette.ink);
  root.style.setProperty("--evolys-brand-surface", palette.surface);
  root.style.setProperty("--evolys-brand-muted", palette.muted);
}

export function BrandThemeProvider({ children }: { children: ReactNode }) {
  const [paletteKey, setPaletteState] = useState<BrandPaletteKey>(readInitialPalette);
  const palette = BRAND_PALETTES[paletteKey];

  const setPaletteKey = useCallback((key: BrandPaletteKey) => {
    setPaletteState(key);
    persistPalette(key);
  }, []);

  useEffect(() => {
    applyPaletteVariables(palette);
  }, [palette]);

  const value = useMemo(
    () => ({ paletteKey, palette, setPaletteKey }),
    [paletteKey, palette, setPaletteKey],
  );

  return <BrandThemeContext.Provider value={value}>{children}</BrandThemeContext.Provider>;
}

export function useBrandTheme() {
  const context = useContext(BrandThemeContext);
  if (!context) {
    throw new Error("useBrandTheme must be used inside BrandThemeProvider.");
  }

  return context;
}
